import { useCallback, useEffect, useState } from 'react'
import { useAdmin } from '../../hooks/useAdmin'
import { supabase } from '../../services/supabase'
import {
  SANDBOX_CONFIGURED,
  SANDBOX_SUPABASE_ANON_KEY,
  SANDBOX_SUPABASE_URL,
  SUPABASE_ANON_KEY,
  SUPABASE_URL,
} from '../../utils/constants'

type EnvMode = 'production' | 'sandbox'

type SandboxLifecycle = {
  lifecycleConfigured: boolean
  state: 'active' | 'paused' | 'transitioning' | 'unknown'
  rawStatus: string
  canPause: boolean
  canResume: boolean
  message?: string
  projectRef?: string
}

type ApiKeyRow = {
  id: string
  company_id: string
  name: string
  key_prefix: string
  scopes: string[]
  rate_limit_per_minute: number
  created_at: string
  last_used_at: string | null
  revoked_at: string | null
  grace_until?: string | null
  assigned_only?: boolean
}

type JobRow = {
  id: string
  company_id: string
  status: string
  created_at: string
  finished_at: string | null
  row_counts: Record<string, number> | null
  error: string | null
}

type FixtureStatus = {
  seeded: boolean
  matchesBaseline?: boolean
  counts?: Record<string, number>
  expected?: Record<string, number>
  checklist?: string[]
  message?: string
}

function graceCountdown(graceUntil: string | null | undefined): string | null {
  if (!graceUntil) return null
  const ms = new Date(graceUntil).getTime() - Date.now()
  if (ms <= 0) return 'Grace expired (will revoke on next use)'
  const h = Math.floor(ms / 3600000)
  const m = Math.floor((ms % 3600000) / 60000)
  return `Grace ${h}h ${m}m left`
}

async function callSandboxProxy(path: string, init: RequestInit = {}) {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not signed in')
  const url = `${SUPABASE_URL}/functions/v1/sandbox-api-proxy${path}`
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(json?.error?.message || json?.error || `HTTP ${res.status}`)
  }
  return json
}

async function callExportApi(path: string, init: RequestInit = {}) {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not signed in')
  const url = `${SUPABASE_URL}/functions/v1/early-signals-export${path}`
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(json?.error?.message || json?.error || `HTTP ${res.status}`)
  }
  return json
}

const SCOPES = [
  { id: '*', label: 'All datasets' },
  { id: 'intelligence-view', label: 'Intelligence view' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'mitigation-tracker', label: 'Mitigation tracker' },
  { id: 'special-reports', label: 'Special reports' },
]

/**
 * Admin panel: company API keys + recent export jobs for Early Signals Data API.
 */
export default function DataApiPanel() {
  const { companies, companiesLoading } = useAdmin()
  const [envMode, setEnvMode] = useState<EnvMode>('production')
  const [sandboxCompanies, setSandboxCompanies] = useState<{ id: string; name: string; domain?: string }[]>([])
  const [companyId, setCompanyId] = useState('')
  const [keys, setKeys] = useState<ApiKeyRow[]>([])
  const [jobs, setJobs] = useState<JobRow[]>([])
  const [name, setName] = useState('Partner – Production')
  const [scopes, setScopes] = useState<string[]>(['*'])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [newSecret, setNewSecret] = useState<string | null>(null)
  const [curlHint, setCurlHint] = useState('')
  const [guideOpen, setGuideOpen] = useState(false)
  const [itHelpOpen, setItHelpOpen] = useState(false)
  const [sandboxGuideOpen, setSandboxGuideOpen] = useState(false)
  const [sandboxLifecycle, setSandboxLifecycle] = useState<SandboxLifecycle | null>(null)
  const [lifecycleLoading, setLifecycleLoading] = useState(false)
  const [fixtureStatus, setFixtureStatus] = useState<FixtureStatus | null>(null)
  const [assignedOnly, setAssignedOnly] = useState(false)
  const [fixtureResetting, setFixtureResetting] = useState(false)

  const isSandbox = envMode === 'sandbox'
  const keyPrefix = isSandbox ? 'es_test_' : 'es_live_'
  const exportBase = isSandbox
    ? `${SANDBOX_SUPABASE_URL.replace(/\/$/, '')}/functions/v1/early-signals-export`
    : `${SUPABASE_URL}/functions/v1/early-signals-export`
  const publicAccessCode = isSandbox ? SANDBOX_SUPABASE_ANON_KEY : SUPABASE_ANON_KEY
  const companyOptions = (() => {
    if (!isSandbox) return [...(companies || [])]
    // Partner UAT is Sandbox Demo Co only; hide leftover branch companies (e.g. Synapsys/TSI).
    return [...sandboxCompanies]
      .filter((c) => c.domain === 'sandbox-demo.example' || c.name === 'Sandbox Demo Co')
      .sort((a, b) => a.name.localeCompare(b.name))
  })()
  const sandboxPaused = isSandbox && sandboxLifecycle?.state === 'paused'
  const sandboxTransitioning = isSandbox && sandboxLifecycle?.state === 'transitioning'
  const keyNameExample = isSandbox ? 'Acme – Sandbox UAT' : 'Acme – Production'

  const loadLifecycle = useCallback(async () => {
    if (!SANDBOX_CONFIGURED) return
    setLifecycleLoading(true)
    try {
      const data = await callSandboxProxy('/lifecycle', { method: 'GET' })
      setSandboxLifecycle({
        lifecycleConfigured: !!data.lifecycleConfigured,
        state: data.state || 'unknown',
        rawStatus: data.rawStatus || 'UNKNOWN',
        canPause: !!data.canPause,
        canResume: !!data.canResume,
        message: data.message,
        projectRef: data.projectRef,
      })
    } catch (e) {
      setSandboxLifecycle({
        lifecycleConfigured: false,
        state: 'unknown',
        rawStatus: 'UNKNOWN',
        canPause: false,
        canResume: false,
        message: e instanceof Error ? e.message : 'Could not load sandbox status',
      })
    } finally {
      setLifecycleLoading(false)
    }
  }, [])

  const runLifecycleAction = async (action: 'pause' | 'resume') => {
    const label = action === 'pause' ? 'pause' : 'resume'
    const ok = confirm(
      action === 'pause'
        ? 'Pause the sandbox? Partner test API calls will stop and hourly sandbox cost should stop. You can resume later from this page.'
        : 'Resume the sandbox? It may take about a minute to come back online. Hourly cost will start again.'
    )
    if (!ok) return
    setLifecycleLoading(true)
    setError(null)
    try {
      const data = await callSandboxProxy('/lifecycle', {
        method: 'POST',
        body: JSON.stringify({ action }),
      })
      setSandboxLifecycle({
        lifecycleConfigured: !!data.lifecycleConfigured,
        state: data.state || 'unknown',
        rawStatus: data.rawStatus || 'UNKNOWN',
        canPause: !!data.canPause,
        canResume: !!data.canResume,
        message: data.message,
        projectRef: data.projectRef,
      })
      if (action === 'resume') {
        await new Promise((r) => setTimeout(r, 3000))
        await load()
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to ${label} sandbox`)
    } finally {
      setLifecycleLoading(false)
      void loadLifecycle()
    }
  }

  useEffect(() => {
    if (isSandbox) void loadLifecycle()
  }, [isSandbox, loadLifecycle])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (isSandbox) {
        if (!SANDBOX_CONFIGURED) {
          throw new Error('Sandbox is not configured yet')
        }
        const coRes = await callSandboxProxy('/companies', { method: 'GET' })
        const cos = coRes.companies || []
        setSandboxCompanies(cos)
        const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : ''
        const keyRes = await callSandboxProxy(`/keys${qs}`, { method: 'GET' })
        setKeys(keyRes.keys || [])
        try {
          const jobRes = await callSandboxProxy(`/jobs${qs}`, { method: 'GET' })
          setJobs(jobRes.jobs || [])
        } catch {
          setJobs([])
        }
        try {
          const fix = await callSandboxProxy('/fixtures', { method: 'GET' })
          setFixtureStatus(fix)
        } catch {
          setFixtureStatus(null)
        }
        return
      }
      setFixtureStatus(null)
      const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : ''
      const keyRes = await callExportApi(`/keys${qs}`, { method: 'GET' })
      setKeys(keyRes.keys || [])
      const { data: jobRows } = await supabase
        .from('early_signals_export_jobs')
        .select('id, company_id, status, created_at, finished_at, row_counts, error')
        .order('created_at', { ascending: false })
        .limit(30)
      setJobs((jobRows as JobRow[]) || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [companyId, isSandbox])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!companyId && companyOptions?.[0]?.id) setCompanyId(companyOptions[0].id)
  }, [companyOptions, companyId, envMode])

  useEffect(() => {
    setCompanyId('')
    setKeys([])
    setJobs([])
    setNewSecret(null)
    setName(envMode === 'sandbox' ? 'Partner – Sandbox UAT' : 'Partner – Production')
  }, [envMode])

  useEffect(() => {
    setCurlHint(
      `curl -X POST "${exportBase}" \\\n  -H "Authorization: Bearer ${keyPrefix}YOUR_SECRET" \\\n  -H "apikey: ${publicAccessCode}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"datasets":["intelligence-view"],"format":"json","pageSize":5}'`
    )
  }, [exportBase, keyPrefix, publicAccessCode])

  const resetFixtures = async () => {
    const ok = confirm(
      'This replaces synthetic UAT data and deletes all Sandbox Demo Co API keys. Partners will need a new key afterward. Continue?'
    )
    if (!ok) return
    setFixtureResetting(true)
    setError(null)
    try {
      const fix = await callSandboxProxy('/fixtures', {
        method: 'POST',
        body: JSON.stringify({ action: 'wipe' }),
      })
      setFixtureStatus(fix)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reset fixtures')
    } finally {
      setFixtureResetting(false)
    }
  }

  const toggleScope = (id: string) => {
    if (id === '*') {
      setScopes(['*'])
      return
    }
    setScopes((prev) => {
      const withoutStar = prev.filter((s) => s !== '*')
      if (withoutStar.includes(id)) {
        const next = withoutStar.filter((s) => s !== id)
        return next.length ? next : ['*']
      }
      return [...withoutStar, id]
    })
  }

  const createKey = async () => {
    if (!companyId) return
    setLoading(true)
    setError(null)
    setNewSecret(null)
    try {
      const res = isSandbox
        ? await callSandboxProxy('/keys', {
            method: 'POST',
            body: JSON.stringify({ action: 'create', companyId, name, scopes, assignedOnly }),
          })
        : await callExportApi('/keys', {
            method: 'POST',
            body: JSON.stringify({ action: 'create', companyId, name, scopes, assignedOnly }),
          })
      setNewSecret(res.secret)
      if (isSandbox && res.nudge) {
        // Surface wipe/pause reminder alongside the secret
        setError(null)
      }
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed')
    } finally {
      setLoading(false)
    }
  }

  const revokeKey = async (keyId: string) => {
    if (!confirm('Revoke this API key? ETL clients using it will fail immediately.')) return
    setLoading(true)
    try {
      if (isSandbox) {
        await callSandboxProxy('/keys', {
          method: 'POST',
          body: JSON.stringify({ action: 'revoke', keyId }),
        })
      } else {
        await callExportApi('/keys', {
          method: 'POST',
          body: JSON.stringify({ action: 'revoke', keyId }),
        })
      }
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Revoke failed')
    } finally {
      setLoading(false)
    }
  }

  const rotateKey = async (keyId: string) => {
    setLoading(true)
    setNewSecret(null)
    try {
      const res = isSandbox
        ? await callSandboxProxy('/keys', {
            method: 'POST',
            body: JSON.stringify({ action: 'rotate', keyId }),
          })
        : await callExportApi('/keys', {
            method: 'POST',
            body: JSON.stringify({ action: 'rotate', keyId }),
          })
      setNewSecret(res.secret)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Rotate failed')
    } finally {
      setLoading(false)
    }
  }

  const copyPartnerPackage = () => {
    const text = [
      isSandbox ? 'Early Signals Data API — SANDBOX (UAT)' : 'Early Signals Data API — Production',
      '',
      `Data URL: ${exportBase}`,
      `Public access code: ${publicAccessCode}`,
      `Secret key: ${newSecret || `${keyPrefix}<paste after creating key>`}`,
      `Datasets: ${scopes.includes('*') ? 'All datasets' : scopes.join(', ')}`,
      assignedOnly ? 'Visibility: Assigned suppliers only (excludes global / unassigned rows)' : '',
      isSandbox ? '\nNote: Sandbox returns synthetic test data only.' : '',
    ]
      .filter(Boolean)
      .join('\n')
    void navigator.clipboard.writeText(text)
  }

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Early Signals Data API</h2>
        <p className="text-sm text-gray-600 mt-1">
          Create password-like keys so a partner’s IT team can download Early Signals data for their
          company (Intelligence view, Alerts, Mitigation tracker, Special reports). The long secret is
          shown once. Only <span className="font-medium">@thesimpleintel.com</span> admins can create
          keys here.
        </p>
        <div className="mt-4 inline-flex rounded-lg border bg-white p-1 text-sm">
          <button
            type="button"
            className={`px-3 py-1.5 rounded-md ${!isSandbox ? 'bg-black text-white' : 'text-gray-700'}`}
            onClick={() => setEnvMode('production')}
          >
            Production
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 rounded-md ${isSandbox ? 'bg-black text-white' : 'text-gray-700'}`}
            onClick={() => setEnvMode('sandbox')}
            disabled={!SANDBOX_CONFIGURED}
          >
            Sandbox
          </button>
        </div>
        {isSandbox && (
          <p className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 max-w-3xl">
            Sandbox uses synthetic test data only ({keyPrefix} keys). Give partners sandbox credentials
            for UAT; issue production keys after go-live. Expand the sandbox guide below for cost and
            workflow.
          </p>
        )}
        {!isSandbox && SANDBOX_CONFIGURED && (
          <p className="mt-2 text-xs text-gray-600 max-w-3xl">
            Partners should test on the <button type="button" className="underline font-medium" onClick={() => setEnvMode('sandbox')}>Sandbox</button> tab first, then receive production keys here after go-live.
          </p>
        )}
      </div>

      {isSandbox && (
        <section className="border rounded-lg bg-white p-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-900">Sandbox status</p>
            <p className="text-xs text-gray-600">
              {lifecycleLoading
                ? 'Checking…'
                : sandboxLifecycle?.message ||
                  (sandboxLifecycle?.state === 'active'
                    ? 'Running (~$0.01344/hour while active)'
                    : 'Status unknown')}
            </p>
            {sandboxLifecycle?.projectRef && (
              <p className="text-[11px] text-gray-400 font-mono">ref: {sandboxLifecycle.projectRef}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`text-xs font-medium px-2 py-1 rounded ${
                sandboxLifecycle?.state === 'active'
                  ? 'bg-green-100 text-green-800'
                  : sandboxLifecycle?.state === 'paused'
                    ? 'bg-gray-200 text-gray-700'
                    : sandboxLifecycle?.state === 'transitioning'
                      ? 'bg-amber-100 text-amber-900'
                      : 'bg-gray-100 text-gray-600'
              }`}
            >
              {sandboxLifecycle?.state === 'active'
                ? 'Active'
                : sandboxLifecycle?.state === 'paused'
                  ? 'Paused'
                  : sandboxLifecycle?.state === 'transitioning'
                    ? 'Starting / stopping…'
                    : 'Unknown'}
            </span>
            {sandboxLifecycle?.canPause && (
              <button
                type="button"
                disabled={lifecycleLoading}
                onClick={() => void runLifecycleAction('pause')}
                className="px-3 py-1.5 rounded border border-amber-600 text-amber-900 text-sm hover:bg-amber-50 disabled:opacity-50"
              >
                Pause sandbox
              </button>
            )}
            {sandboxLifecycle?.canResume && (
              <button
                type="button"
                disabled={lifecycleLoading}
                onClick={() => void runLifecycleAction('resume')}
                className="px-3 py-1.5 rounded bg-black text-white text-sm disabled:opacity-50"
              >
                Resume sandbox
              </button>
            )}
            {!sandboxLifecycle?.lifecycleConfigured && !lifecycleLoading && (
              <span className="text-xs text-gray-500 max-w-xs">
                Pause/resume uses SB_MANAGEMENT_TOKEN on production edge secrets.
              </span>
            )}
            <button
              type="button"
              className="text-xs underline text-gray-600"
              disabled={lifecycleLoading}
              onClick={() => void loadLifecycle()}
            >
              Refresh
            </button>
          </div>
        </section>
      )}

      {sandboxPaused && (
        <div className="rounded border border-gray-300 bg-gray-50 text-gray-800 text-sm px-3 py-2">
          Sandbox is paused — keys and test exports are unavailable until you click{' '}
          <span className="font-medium">Resume sandbox</span>.
        </div>
      )}

      {isSandbox && fixtureStatus && (
        <section className="border rounded-lg p-4 bg-amber-50/50 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold text-sm">Sandbox fixture checklist</h3>
            <button
              type="button"
              className="text-xs px-3 py-1.5 rounded border border-amber-700 bg-white text-amber-950 hover:bg-amber-100 disabled:opacity-50"
              disabled={fixtureResetting || loading || sandboxPaused || sandboxTransitioning}
              onClick={() => void resetFixtures()}
            >
              {fixtureResetting ? 'Resetting…' : 'Reset fixtures'}
            </button>
          </div>
          {fixtureStatus.message && !fixtureStatus.seeded && (
            <p className="text-xs text-amber-900">{fixtureStatus.message}</p>
          )}
          {fixtureStatus.counts && fixtureStatus.expected && (
            <div className="overflow-x-auto border rounded bg-white text-xs">
              <table className="min-w-full">
                <thead className="bg-gray-50 text-left uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Dataset</th>
                    <th className="px-3 py-2">Count</th>
                    <th className="px-3 py-2">Expected</th>
                  </tr>
                </thead>
                <tbody>
                  {(
                    [
                      ['intelligenceView', 'Intelligence view'],
                      ['alerts', 'Alerts'],
                      ['mitigationTracker', 'Mitigation tracker'],
                      ['specialReports', 'Special reports'],
                    ] as const
                  ).map(([key, label]) => (
                    <tr key={key} className="border-t">
                      <td className="px-3 py-2">{label}</td>
                      <td className="px-3 py-2 font-mono">{fixtureStatus.counts?.[key] ?? '—'}</td>
                      <td className="px-3 py-2 font-mono">{fixtureStatus.expected?.[key] ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p
            className={`text-xs ${
              fixtureStatus.matchesBaseline ? 'text-green-800' : 'text-amber-900'
            }`}
          >
            {fixtureStatus.matchesBaseline
              ? 'Baseline fixtures look good — safe to issue partner package.'
              : 'Counts do not match baseline. Click Reset fixtures before packaging.'}
          </p>
        </section>
      )}

      {!isSandbox && (
        <section className="border rounded-lg bg-slate-50">
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 text-left"
            onClick={() => setGuideOpen((o) => !o)}
            aria-expanded={guideOpen}
          >
            <span className="font-semibold text-gray-900">
              Admin guide — create &amp; send keys {isSandbox ? '(production & sandbox)' : '(production)'}
            </span>
            <span className="text-xs text-gray-500">{guideOpen ? 'Hide' : 'Show'}</span>
          </button>
          {guideOpen && (
            <div className="px-4 pb-5 space-y-6 text-sm text-gray-700 border-t border-slate-200 pt-4">
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Production vs sandbox</h3>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>
                    <span className="font-medium">Sandbox tab</span> — shared test environment,{' '}
                    <code className="text-[11px]">es_test_</code> keys, fake data. Use for partner UAT.
                  </li>
                  <li>
                    <span className="font-medium">Production tab</span> — live data,{' '}
                    <code className="text-[11px]">es_live_</code> keys. Issue only after sandbox testing
                    succeeds.
                  </li>
                  <li>One sandbox for the whole platform; one secret key per partner integration.</li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">What this is</h3>
                <p>
                  Sometimes a partner wants Early Signals data inside their own systems. You create a
                  secret key for <span className="font-medium">their company only</span>. Their IT uses
                  that key to download data. They never get access to our database or other companies’
                  data.
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Who does what</h3>
                <div className="overflow-x-auto border rounded bg-white">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2">Who</th>
                        <th className="px-3 py-2">What they can do</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t">
                        <td className="px-3 py-2">You (TSI admin, @thesimpleintel.com)</td>
                        <td className="px-3 py-2">Create, replace, and turn off keys on this page</td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2">Partner / customer users</td>
                        <td className="px-3 py-2">Cannot create keys themselves</td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2">Their IT team</td>
                        <td className="px-3 py-2">Uses only the secret key you send them</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-600">
                  Make one key per connection (for example one for production and one for testing).
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">When to create a key</h3>
                <p>
                  Create a key when a partner’s IT team asks to connect Early Signals to their systems.
                  Prefer checking only the product areas they need (Intelligence view, Alerts, and so on)
                  instead of “All datasets.”
                </p>
                <p className="font-medium text-gray-900">Never send partners:</p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>Anything labeled “service role” or a database password</li>
                  <li>Direct access to our database</li>
                  <li>A key that belongs to a different company</li>
                  <li>Another partner’s secret (the long code that starts with {keyPrefix})</li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Create a key (step by step)</h3>
                <ol className="list-decimal pl-5 space-y-1">
                <li>Select the partner company in Create key.</li>
                <li>Name it something clear, like “{keyNameExample}.”</li>
                  <li>Choose which data areas they need (or All datasets).</li>
                  <li>
                    Optional: check <span className="font-medium">Assigned suppliers only</span> to
                    exclude global / unassigned rows (default includes company-assigned plus globals).
                  </li>
                  <li>Click Create API key.</li>
                  <li>
                    Copy the long secret that starts with <code className="text-xs">{keyPrefix}</code> right
                    away — you will not see it again.
                  </li>
                </ol>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">What to send the partner</h3>
                <p className="text-xs text-gray-600">
                  Save the secret in 1Password or another vault. Do not leave it in Slack or email for the
                  long term. Send this package over a secure channel.
                </p>
                <div className="overflow-x-auto border rounded bg-white">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2">What</th>
                        <th className="px-3 py-2">What to send</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t">
                        <td className="px-3 py-2 font-medium whitespace-nowrap">Data link (URL)</td>
                        <td className="px-3 py-2">
                          <code className="text-xs break-all">{exportBase}</code>
                          <button
                            type="button"
                            className="ml-2 text-xs underline"
                            onClick={() => void navigator.clipboard.writeText(exportBase)}
                          >
                            Copy
                          </button>
                        </td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2 font-medium whitespace-nowrap">Their secret key</td>
                        <td className="px-3 py-2 text-xs">
                          The <code>{keyPrefix}…</code> value from the yellow “copy now” box after you create
                          or replace a key
                        </td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2 font-medium whitespace-nowrap">Public access code</td>
                        <td className="px-3 py-2 text-xs">
                          The long public code below (safe to share). Their IT needs it together with the
                          secret key.
                        </td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2 font-medium whitespace-nowrap">Which data they can get</td>
                        <td className="px-3 py-2 text-xs">
                          Match the checkboxes you chose: All datasets, or Intelligence view, Alerts,
                          Mitigation tracker, Special reports
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <p className="font-medium text-gray-900 mb-1">Public access code (copy and send)</p>
                  <code className="block text-xs break-all bg-white border rounded p-2">{publicAccessCode}</code>
                  <button
                    type="button"
                    className="mt-1 text-xs underline"
                    onClick={() => void navigator.clipboard.writeText(publicAccessCode)}
                  >
                    Copy public access code
                  </button>
                  <p className="mt-1 text-xs text-gray-500">
                    This is a public code — safe for partners. Never send anything labeled “service role.”
                  </p>
                </div>
              </div>

              <div className="border rounded bg-white">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-3 py-2 text-left"
                  onClick={() => setItHelpOpen((o) => !o)}
                  aria-expanded={itHelpOpen}
                >
                  <span className="font-semibold text-gray-900">For their IT team (optional)</span>
                  <span className="text-xs text-gray-500">{itHelpOpen ? 'Hide' : 'Show'}</span>
                </button>
                {itHelpOpen && (
                  <div className="px-3 pb-3 space-y-2 border-t border-gray-100">
                    <p className="text-xs text-gray-600">
                      You do not need to run this. Copy it and send it to their engineer so they can test
                      the connection. Replace <code>{keyPrefix}YOUR_SECRET</code> with the real secret you
                      created.
                    </p>
                    <pre className="text-xs bg-gray-50 border rounded p-3 overflow-x-auto whitespace-pre-wrap">
                      {curlHint}
                    </pre>
                    <button
                      type="button"
                      className="text-xs underline"
                      onClick={() => void navigator.clipboard.writeText(curlHint)}
                    >
                      Copy example for their IT
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">After you send it</h3>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>
                    Ask their IT to confirm the connection works (they will know how to test it).
                  </li>
                  <li>
                  Write it down in your tracker: your name, company, key prefix from the Keys table
                  (first characters), which data areas, date, and partner contact.
                  </li>
                </ol>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Replace a key (Rotate)</h3>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Click Rotate on an active key in the Keys table.</li>
                  <li>Copy the new secret once.</li>
                  <li>Send the new secret to the partner so their IT can update their system.</li>
                  <li>
                    The old key stays valid for about 24 hours (Status shows a grace countdown), then is
                    auto-revoked.
                  </li>
                </ol>
                <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  You can still click Revoke on the old key immediately if you need to cut off access
                  sooner than the grace period.
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Turn a key off (Revoke)</h3>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>
                    Click Revoke (and confirm) if the secret leaked, the partner is leaving, or after
                    they finished switching to a new key.
                  </li>
                  <li>Their connection stops working immediately.</li>
                  <li>If they still need access, create a new key and notify them.</li>
                </ol>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">What “All datasets” means</h3>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>
                    The checkboxes only control which Early Signals product areas the key can download.
                  </li>
                  <li>
                    By default a key sees that company’s assigned suppliers plus global (unassigned)
                    suppliers — not other companies’ data. Check{' '}
                    <span className="font-medium">Assigned suppliers only</span> to exclude globals.
                  </li>
                  <li>Each key can make about 60 requests per minute.</li>
                  <li>
                    Full multi-dataset export (<code className="text-[11px]">datasets: [&quot;*&quot;]</code>
                    ) queues an async job — partners poll the job id (see engineer docs).
                  </li>
                </ul>
              </div>
            </div>
          )}
        </section>
      )}


      {error && (
        <div className="rounded border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2">
          {error}
        </div>
      )}

      <section className="space-y-3 border rounded-lg p-4">
        <h3 className="font-semibold">Create key {isSandbox ? '(sandbox)' : '(production)'}</h3>
        {isSandbox && sandboxPaused && (
          <p className="text-xs text-amber-900 bg-amber-100 border border-amber-200 rounded px-2 py-1.5">
            Sandbox is paused — resume before creating <code className="text-[11px]">es_test_</code> keys.
          </p>
        )}
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm">
            Company
            <select
              className="block mt-1 border rounded px-2 py-1.5 min-w-[220px]"
              value={companyId}
              disabled={companiesLoading && !isSandbox}
              onChange={(e) => setCompanyId(e.target.value)}
            >
              {companyOptions.map((c: { id: string; name: string }) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {isSandbox && (
              <span className="block mt-1 text-xs text-gray-600">
                For partner UAT, use <span className="font-medium">Sandbox Demo Co</span>.
              </span>
            )}
          </label>
          <label className="text-sm">
            Name
            <input
              className="block mt-1 border rounded px-2 py-1.5"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <button
            type="button"
            disabled={loading || !companyId || sandboxPaused || sandboxTransitioning}
            onClick={() => void createKey()}
            className="px-3 py-1.5 rounded bg-black text-white text-sm disabled:opacity-50"
          >
            Create API key
          </button>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {SCOPES.map((s) => (
            <label key={s.id} className="inline-flex items-center gap-1 border rounded px-2 py-1">
              <input
                type="checkbox"
                checked={scopes.includes(s.id)}
                onChange={() => toggleScope(s.id)}
              />
              {s.label}
            </label>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-gray-700">
          <input
            type="checkbox"
            checked={assignedOnly}
            onChange={(e) => setAssignedOnly(e.target.checked)}
          />
          Assigned suppliers only (exclude global / unassigned rows)
        </label>
      </section>

      {newSecret && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-3 text-sm">
          <p className="font-semibold text-amber-900">Copy this secret now — it will not be shown again.</p>
          <code className="block mt-2 break-all bg-white border rounded p-2 text-xs">{newSecret}</code>
          <button
            type="button"
            className="mt-2 text-xs underline"
            onClick={() => navigator.clipboard.writeText(newSecret)}
          >
            Copy to clipboard
          </button>
          <button
            type="button"
            className="ml-2 text-xs underline"
            onClick={() => void copyPartnerPackage()}
          >
            Copy partner package
          </button>
        </div>
      )}

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Keys</h3>
          <button type="button" className="text-sm underline" onClick={() => void load()} disabled={loading}>
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto border rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Prefix</th>
                <th className="px-3 py-2">Scopes</th>
                <th className="px-3 py-2">Last used</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className="border-t">
                  <td className="px-3 py-2">
                    {k.name}
                    {k.assigned_only ? (
                      <span className="ml-1 text-[10px] uppercase bg-slate-100 text-slate-700 px-1 rounded">
                        assigned
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    <span
                      className={`mr-1 px-1 rounded text-[10px] uppercase ${
                        k.key_prefix.startsWith('es_test_')
                          ? 'bg-amber-100 text-amber-900'
                          : 'bg-green-100 text-green-900'
                      }`}
                    >
                      {k.key_prefix.startsWith('es_test_') ? 'Test' : 'Live'}
                    </span>
                    {k.key_prefix}…
                  </td>
                  <td className="px-3 py-2 text-xs">{(k.scopes || []).join(', ')}</td>
                  <td className="px-3 py-2 text-xs">
                    {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {k.revoked_at ? (
                      <span className="text-red-600">Revoked</span>
                    ) : graceCountdown(k.grace_until) ? (
                      <span className="text-amber-800" title={k.grace_until || undefined}>
                        {graceCountdown(k.grace_until)}
                      </span>
                    ) : (
                      <span className="text-green-700">Active</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right space-x-2">
                    {!k.revoked_at && (
                      <>
                        <button type="button" className="text-xs underline" onClick={() => void rotateKey(k.id)}>
                          Rotate
                        </button>
                        <button
                          type="button"
                          className="text-xs underline text-red-700"
                          onClick={() => void revokeKey(k.id)}
                        >
                          Revoke
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {!keys.length && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                    No keys yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="font-semibold">Recent export jobs {isSandbox ? '(sandbox)' : ''}</h3>
        <div className="overflow-x-auto border rounded">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Job</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2">Rows</th>
                <th className="px-3 py-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs">{j.id.slice(0, 8)}…</td>
                  <td className="px-3 py-2">{j.status}</td>
                  <td className="px-3 py-2 text-xs">{new Date(j.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2 text-xs">
                    {j.row_counts ? JSON.stringify(j.row_counts) : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-red-700">{j.error || '—'}</td>
                </tr>
              ))}
              {!jobs.length && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                    No jobs yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {isSandbox && (
        <section className="border rounded-lg bg-amber-50/80 border-amber-200">
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 text-left"
            onClick={() => setSandboxGuideOpen((o) => !o)}
            aria-expanded={sandboxGuideOpen}
          >
            <span className="font-semibold text-gray-900">Sandbox guide — testing, cost &amp; clients</span>
            <span className="text-xs text-gray-500">{sandboxGuideOpen ? 'Hide' : 'Show'}</span>
          </button>
          {sandboxGuideOpen && (
            <div className="px-4 pb-5 space-y-5 text-sm text-gray-700 border-t border-amber-200 pt-4">
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Is it one sandbox per client?</h3>
                <p>
                  <span className="font-medium">No.</span> There is <span className="font-medium">one shared sandbox environment</span> for
                  The Simple Intel (one test URL for all partners). You do <span className="font-medium">not</span> create a
                  separate sandbox per customer — that would multiply cost and ops work.
                </p>
                <p>
                  Per client you create a <span className="font-medium">separate sandbox API key</span> ({keyPrefix}…) scoped to
                  the company you choose under Create key above. Keys are isolated: one partner cannot use another
                  partner’s key. Data in sandbox is <span className="font-medium">synthetic</span> (fake suppliers/alerts), not
                  their real production records.
                </p>
                <div className="overflow-x-auto border rounded bg-white text-xs">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 text-left uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2">What</th>
                        <th className="px-3 py-2">Shared or per client?</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t">
                        <td className="px-3 py-2">Sandbox URL &amp; public access code</td>
                        <td className="px-3 py-2">Shared (same for all partners)</td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2">Secret API key ({keyPrefix}…)</td>
                        <td className="px-3 py-2">Per client / per integration (create one per partner)</td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2">Test data (suppliers, alerts, etc.)</td>
                        <td className="px-3 py-2">Shared synthetic fixtures (Sandbox Demo Co)</td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2">Production URL &amp; key (es_live_…)</td>
                        <td className="px-3 py-2">Per client — created on Production tab after UAT</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">What sandbox is for</h3>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>Partner IT validates connection, auth, pagination, CSV/JSON, and error handling</li>
                  <li>UAT without touching real customer data or production rate limits</li>
                  <li>Safe place to rotate/revoke test keys before issuing production keys</li>
                </ul>
                <p className="text-xs text-gray-600">
                  Sandbox is <span className="font-medium">not</span> for checking whether their real supplier list matches
                  production — use production keys only after go-live for that.
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Cost (important)</h3>
                <p>
                  The sandbox runs on a separate Supabase branch that is billed{' '}
                  <span className="font-medium">by the hour while it stays running</span> — not a one-time setup fee
                  and not a lifetime product charge.
                </p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>About <span className="font-medium">$0.01344/hour</span> (~<span className="font-medium">$10/month</span> if left on 24/7)</li>
                  <li>Charge accrues from when the branch exists until it is deleted or turned off in Supabase</li>
                  <li>Production app and production database billing are separate and unchanged</li>
                </ul>
                <p className="text-xs text-amber-900 bg-amber-100 border border-amber-200 rounded px-2 py-1.5">
                  If no partner is actively integrating, use <span className="font-medium">Pause sandbox</span> above
                  to stop hourly cost (~$10/month if left running 24/7). Resume when the next partner needs UAT.
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Step-by-step — send sandbox access to a partner</h3>
                <ol className="list-decimal pl-5 space-y-1 text-xs">
                  <li>
                    <span className="font-medium">Resume sandbox</span> if the status badge shows paused.
                  </li>
                  <li>
                    Click <span className="font-medium">Reset fixtures</span> if counts do not match
                    baseline (or before a clean partner UAT). Confirm counts in the fixture checklist
                    above.
                  </li>
                  <li>Stay on this <span className="font-medium">Sandbox</span> tab (not Production).</li>
                  <li>
                    Under <span className="font-medium">Create key</span>, select{' '}
                    <span className="font-medium">Sandbox Demo Co</span> (shared test company with fake data).
                  </li>
                  <li>
                    Name the key clearly, e.g. <code className="text-[11px]">Acme – Sandbox UAT</code>.
                  </li>
                  <li>Choose dataset scopes (or All datasets). Optional: Assigned suppliers only.</li>
                  <li>
                    Click <span className="font-medium">Create API key</span> and copy the {keyPrefix} secret immediately.
                  </li>
                  <li>
                    After Create API key, use <span className="font-medium">Copy partner package</span> in
                    the yellow secret box (includes Data URL, public access code, and secret).
                  </li>
                  <li>Send via a secure channel (1Password, encrypted email — not long-term Slack).</li>
                  <li>Ask their IT to confirm a small test export works before you issue production keys.</li>
                  <li>
                    When no partner is testing, use <span className="font-medium">Pause sandbox</span> to stop hourly cost.
                  </li>
                </ol>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">After UAT passes</h3>
                <ol className="list-decimal pl-5 space-y-1 text-xs">
                  <li>Switch to the <span className="font-medium">Production</span> tab.</li>
                  <li>Select the partner’s real company (e.g. Kenvue).</li>
                  <li>Create a new <code className="text-[11px]">es_live_</code> key named e.g. <code className="text-[11px]">Acme – Production</code>.</li>
                  <li>
                    Send the production package: production uses a{' '}
                    <span className="font-medium">different Data URL</span> and an{' '}
                    <code className="text-[11px]">es_live_</code> key (not the sandbox URL or{' '}
                    <code className="text-[11px]">es_test_</code> key).
                  </li>
                  <li>
                    Switch back to the <span className="font-medium">Sandbox</span> tab and revoke that
                    partner’s <code className="text-[11px]">es_test_</code> key when they no longer need UAT.
                  </li>
                </ol>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Sandbox credentials (copy &amp; send)</h3>
                <div className="overflow-x-auto border rounded bg-white text-xs">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 text-left uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2">Field</th>
                        <th className="px-3 py-2">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t">
                        <td className="px-3 py-2 font-medium whitespace-nowrap">Data URL</td>
                        <td className="px-3 py-2">
                          <code className="break-all">{exportBase}</code>
                          <button type="button" className="ml-2 underline" onClick={() => void navigator.clipboard.writeText(exportBase)}>Copy</button>
                        </td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2 font-medium">Public access code</td>
                        <td className="px-3 py-2">
                          <code className="break-all block max-h-16 overflow-y-auto">{publicAccessCode}</code>
                          <button type="button" className="mt-1 underline" onClick={() => void navigator.clipboard.writeText(publicAccessCode)}>Copy</button>
                        </td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2 font-medium">Secret key</td>
                        <td className="px-3 py-2">Create above — starts with {keyPrefix} — shown once</td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2 font-medium">Tell the partner</td>
                        <td className="px-3 py-2">Data is synthetic; same API shape as production</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {isSandbox && (
        <section className="border rounded-lg bg-slate-50">
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 text-left"
            onClick={() => setGuideOpen((o) => !o)}
            aria-expanded={guideOpen}
          >
            <span className="font-semibold text-gray-900">
              Admin guide — create &amp; send keys {isSandbox ? '(production & sandbox)' : '(production)'}
            </span>
            <span className="text-xs text-gray-500">{guideOpen ? 'Hide' : 'Show'}</span>
          </button>
          {guideOpen && (
            <div className="px-4 pb-5 space-y-6 text-sm text-gray-700 border-t border-slate-200 pt-4">
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Production vs sandbox</h3>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>
                    <span className="font-medium">Sandbox tab</span> — shared test environment,{' '}
                    <code className="text-[11px]">es_test_</code> keys, fake data. Use for partner UAT.
                  </li>
                  <li>
                    <span className="font-medium">Production tab</span> — live data,{' '}
                    <code className="text-[11px]">es_live_</code> keys. Issue only after sandbox testing
                    succeeds.
                  </li>
                  <li>One sandbox for the whole platform; one secret key per partner integration.</li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">What this is</h3>
                <p>
                  Sometimes a partner wants Early Signals data inside their own systems. You create a
                  secret key for <span className="font-medium">their company only</span>. Their IT uses
                  that key to download data. They never get access to our database or other companies’
                  data.
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Who does what</h3>
                <div className="overflow-x-auto border rounded bg-white">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2">Who</th>
                        <th className="px-3 py-2">What they can do</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t">
                        <td className="px-3 py-2">You (TSI admin, @thesimpleintel.com)</td>
                        <td className="px-3 py-2">Create, replace, and turn off keys on this page</td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2">Partner / customer users</td>
                        <td className="px-3 py-2">Cannot create keys themselves</td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2">Their IT team</td>
                        <td className="px-3 py-2">Uses only the secret key you send them</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-600">
                  Make one key per connection (for example one for production and one for testing).
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">When to create a key</h3>
                <p>
                  Create a key when a partner’s IT team asks to connect Early Signals to their systems.
                  Prefer checking only the product areas they need (Intelligence view, Alerts, and so on)
                  instead of “All datasets.”
                </p>
                <p className="font-medium text-gray-900">Never send partners:</p>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>Anything labeled “service role” or a database password</li>
                  <li>Direct access to our database</li>
                  <li>A key that belongs to a different company</li>
                  <li>Another partner’s secret (the long code that starts with {keyPrefix})</li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Create a key (step by step)</h3>
                <ol className="list-decimal pl-5 space-y-1">
                <li>Select the partner company in Create key.</li>
                <li>Name it something clear, like “{keyNameExample}.”</li>
                  <li>Choose which data areas they need (or All datasets).</li>
                  <li>
                    Optional: check <span className="font-medium">Assigned suppliers only</span> to
                    exclude global / unassigned rows (default includes company-assigned plus globals).
                  </li>
                  <li>Click Create API key.</li>
                  <li>
                    Copy the long secret that starts with <code className="text-xs">{keyPrefix}</code> right
                    away — you will not see it again.
                  </li>
                </ol>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900">What to send the partner</h3>
                <p className="text-xs text-gray-600">
                  Save the secret in 1Password or another vault. Do not leave it in Slack or email for the
                  long term. Send this package over a secure channel.
                </p>
                <div className="overflow-x-auto border rounded bg-white">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2">What</th>
                        <th className="px-3 py-2">What to send</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t">
                        <td className="px-3 py-2 font-medium whitespace-nowrap">Data link (URL)</td>
                        <td className="px-3 py-2">
                          <code className="text-xs break-all">{exportBase}</code>
                          <button
                            type="button"
                            className="ml-2 text-xs underline"
                            onClick={() => void navigator.clipboard.writeText(exportBase)}
                          >
                            Copy
                          </button>
                        </td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2 font-medium whitespace-nowrap">Their secret key</td>
                        <td className="px-3 py-2 text-xs">
                          The <code>{keyPrefix}…</code> value from the yellow “copy now” box after you create
                          or replace a key
                        </td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2 font-medium whitespace-nowrap">Public access code</td>
                        <td className="px-3 py-2 text-xs">
                          The long public code below (safe to share). Their IT needs it together with the
                          secret key.
                        </td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-3 py-2 font-medium whitespace-nowrap">Which data they can get</td>
                        <td className="px-3 py-2 text-xs">
                          Match the checkboxes you chose: All datasets, or Intelligence view, Alerts,
                          Mitigation tracker, Special reports
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <p className="font-medium text-gray-900 mb-1">Public access code (copy and send)</p>
                  <code className="block text-xs break-all bg-white border rounded p-2">{publicAccessCode}</code>
                  <button
                    type="button"
                    className="mt-1 text-xs underline"
                    onClick={() => void navigator.clipboard.writeText(publicAccessCode)}
                  >
                    Copy public access code
                  </button>
                  <p className="mt-1 text-xs text-gray-500">
                    This is a public code — safe for partners. Never send anything labeled “service role.”
                  </p>
                </div>
              </div>

              <div className="border rounded bg-white">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-3 py-2 text-left"
                  onClick={() => setItHelpOpen((o) => !o)}
                  aria-expanded={itHelpOpen}
                >
                  <span className="font-semibold text-gray-900">For their IT team (optional)</span>
                  <span className="text-xs text-gray-500">{itHelpOpen ? 'Hide' : 'Show'}</span>
                </button>
                {itHelpOpen && (
                  <div className="px-3 pb-3 space-y-2 border-t border-gray-100">
                    <p className="text-xs text-gray-600">
                      You do not need to run this. Copy it and send it to their engineer so they can test
                      the connection. Replace <code>{keyPrefix}YOUR_SECRET</code> with the real secret you
                      created.
                    </p>
                    <pre className="text-xs bg-gray-50 border rounded p-3 overflow-x-auto whitespace-pre-wrap">
                      {curlHint}
                    </pre>
                    <button
                      type="button"
                      className="text-xs underline"
                      onClick={() => void navigator.clipboard.writeText(curlHint)}
                    >
                      Copy example for their IT
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">After you send it</h3>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>
                    Ask their IT to confirm the connection works (they will know how to test it).
                  </li>
                  <li>
                  Write it down in your tracker: your name, company, key prefix from the Keys table
                  (first characters), which data areas, date, and partner contact.
                  </li>
                </ol>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Replace a key (Rotate)</h3>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>Click Rotate on an active key in the Keys table.</li>
                  <li>Copy the new secret once.</li>
                  <li>Send the new secret to the partner so their IT can update their system.</li>
                  <li>
                    The old key stays valid for about 24 hours (Status shows a grace countdown), then is
                    auto-revoked.
                  </li>
                </ol>
                <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                  You can still click Revoke on the old key immediately if you need to cut off access
                  sooner than the grace period.
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">Turn a key off (Revoke)</h3>
                <ol className="list-decimal pl-5 space-y-1">
                  <li>
                    Click Revoke (and confirm) if the secret leaked, the partner is leaving, or after
                    they finished switching to a new key.
                  </li>
                  <li>Their connection stops working immediately.</li>
                  <li>If they still need access, create a new key and notify them.</li>
                </ol>
              </div>

              <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">What “All datasets” means</h3>
                <ul className="list-disc pl-5 space-y-1 text-xs">
                  <li>
                    The checkboxes only control which Early Signals product areas the key can download.
                  </li>
                  <li>
                    By default a key sees that company’s assigned suppliers plus global (unassigned)
                    suppliers — not other companies’ data. Check{' '}
                    <span className="font-medium">Assigned suppliers only</span> to exclude globals.
                  </li>
                  <li>Each key can make about 60 requests per minute.</li>
                  <li>
                    Full multi-dataset export (<code className="text-[11px]">datasets: [&quot;*&quot;]</code>
                    ) queues an async job — partners poll the job id (see engineer docs).
                  </li>
                </ul>
              </div>
            </div>
          )}
        </section>
      )}

      <section className="space-y-2">
        <h3 className="font-semibold">Example for their IT (optional)</h3>
        <p className="text-xs text-gray-600">
          You do not need to run this. Send it to their engineer if they ask for a test command.
        </p>
        <pre className="text-xs bg-gray-50 border rounded p-3 overflow-x-auto whitespace-pre-wrap">{curlHint}</pre>
        <button
          type="button"
          className="text-xs underline"
          onClick={() => void navigator.clipboard.writeText(curlHint)}
        >
          Copy example
        </button>
      </section>
    </div>
  )
}
